import { useCallback } from 'react';
import { fetchSearchResults } from '../api/matchmakingApi';
import { mapBackendProvidersToApiResponse, mapApiResponseToMatches, type ApiResponse } from '../utils/matchmakingHelpers';
import { preloadProviderLogos, preloadProviderImages } from '../utils/image-preloading.utils';
import { setCachedImages } from '../utils/image-cache.utils';
import {
  extractProviderIds,
  createFilterValuesString,
  createSaveResultsPayload,
} from '../utils/search-payload.utils';
import { callMatchmakingApi } from '../utils/matchmaking-api.utils';
import { PAGE_SIZE } from '../config/matchmaking.config';
import { isGenericSearch } from '../constants/client-selection';
import type { StepperAnswers } from './useMatchmakingStateManagement';

type MatchmakingState = ReturnType<typeof import('./useMatchmakingState').useMatchmakingState>;

type UseLoadMoreProps = {
  matchingPrompt: string;
  hasClients: boolean;
  selectedClientId: string | null;
  currentSearchId: number | null;
  advisorProfileId: string | null;
  loadCount: number;
  stepperAnswers: StepperAnswers;
  apiResults: any[];
  matchmakingState: MatchmakingState;
  preloadedImages: Record<string, string | null>;
  filteredProvidersMutation: any;
  saveResultsMutation: any;
  setIsLoadingMore: (value: boolean) => void;
  setError: (error: string | null) => void;
  setLoadMoreMessage: (message: string | null) => void;
  setLoadCount: (count: number) => void;
  setIsPreloadingImages: (value: boolean) => void;
  setPreloadedImages: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
};

export const useLoadMore = ({
  matchingPrompt,
  hasClients,
  selectedClientId,
  currentSearchId,
  advisorProfileId,
  loadCount,
  stepperAnswers,
  apiResults,
  matchmakingState,
  preloadedImages,
  filteredProvidersMutation,
  saveResultsMutation,
  setIsLoadingMore,
  setError,
  setLoadMoreMessage,
  setLoadCount,
  setIsPreloadingImages,
  setPreloadedImages,
}: UseLoadMoreProps) => {
  const handleLoadMore = useCallback(async () => {
    // Allow load more for generic search OR client-specific search
    const isGeneric = isGenericSearch(selectedClientId);
    const canLoadMore = isGeneric || (hasClients && selectedClientId);

    if (!matchingPrompt.trim() || !canLoadMore || !currentSearchId || !advisorProfileId) {
      return;
    }

    setIsLoadingMore(true);
    setError(null);
    setLoadMoreMessage(null);

    try {
      let filterValues: string | null = null;
      try {
        const filteredProvidersData = await filteredProvidersMutation.mutateAsync({
          searchId: currentSearchId,
          advisorId: advisorProfileId,
          clientId: selectedClientId,
        });

        if (filteredProvidersData.providers && Array.isArray(filteredProvidersData.providers)) {
          const providerIds = extractProviderIds(filteredProvidersData.providers);

          if (providerIds.length > 0) {
            filterValues = createFilterValuesString(providerIds);
          } else {
            matchmakingState.setClientMatches({
              matches: apiResults,
              append: false,
              hasMore: false,
            });
            setIsLoadingMore(false);
            return;
          }
        }
      } catch (filteredProvidersError) {
        console.error('Error fetching filtered providers:', filteredProvidersError);
        setError('Failed to load more results. Please try again.');
        setIsLoadingMore(false);
        return;
      }

      const matchmakingApiData: ApiResponse = await callMatchmakingApi({
        matchingPrompt,
        filterValues,
        answersToUse: stepperAnswers,
      });
      const matchmakingStatusCode = matchmakingApiData.status_code ?? 200;

      if (matchmakingStatusCode === 204) {
        const apiMessage =
          (matchmakingApiData as any)?.result?.message ||
          'No suitable providers found. Please refine your search.';
        setLoadMoreMessage(apiMessage);
        if (selectedClientId) {
          matchmakingState.setClientMatches({
            matches: apiResults,
            append: false,
            hasMore: false,
          });
        }
        setIsLoadingMore(false);
        return;
      }

      const matchResultsRecord = matchmakingApiData.result as Record<string, any>;

      try {
        // TypeScript refinement: we checked these are not null in the guard clause at the start
        const saveResultsPayload = createSaveResultsPayload(
          currentSearchId,
          advisorProfileId!,
          selectedClientId!,
          matchResultsRecord,
        );
        await saveResultsMutation.mutateAsync(saveResultsPayload);
      } catch (saveResultsError) {
        console.error('Error saving results:', saveResultsError);
      }

      const nextLoadCount = loadCount + 1;
      const recordStart = (nextLoadCount - 1) * PAGE_SIZE + 1;
      const recordEnd = nextLoadCount * PAGE_SIZE;

      const backendData = await fetchSearchResults({
        searchId: currentSearchId,
        advisorId: advisorProfileId!,
        clientId: selectedClientId!,
        recordStart,
        recordEnd,
      });

      if (backendData.status && backendData.status.toUpperCase() === 'FAILED') {
        throw new Error(backendData.message || 'Failed to fetch search results from backend.');
      }

      if (!backendData.providers || !Array.isArray(backendData.providers) || backendData.providers.length === 0) {
        matchmakingState.setClientMatches({
          matches: apiResults,
          append: false,
          hasMore: false,
        });
        setIsLoadingMore(false);
        return;
      }

      const apiResponse = mapBackendProvidersToApiResponse(backendData.providers);
      let mappedMatches = mapApiResponseToMatches(apiResponse);
      mappedMatches = await preloadProviderLogos(mappedMatches);

      const existingProviderIds = new Set(apiResults.map((match) => match.id));
      const newMatches = mappedMatches.filter((match) => !existingProviderIds.has(match.id));

      const totalCount = typeof backendData.totalCount === 'number' ? backendData.totalCount : 0;
      const providersReturned = backendData.providers?.length || 0;
      const hasMoreResults = totalCount > recordEnd || providersReturned === PAGE_SIZE;

      setLoadCount(nextLoadCount);
      setIsPreloadingImages(true);
      try {
        const imageMap = await preloadProviderImages(newMatches, preloadedImages);
        // Update cache with newly loaded images
        setCachedImages(imageMap);
        setPreloadedImages((prev) => ({ ...prev, ...imageMap }));
      } catch (error) {
        console.error('Error preloading images for load more:', error);
      } finally {
        setIsPreloadingImages(false);
      }

      if (newMatches.length > 0) {
        matchmakingState.setClientMatches({
          matches: newMatches,
          append: true,
          hasMore: hasMoreResults,
        });
      } else {
        matchmakingState.setClientMatches({
          matches: apiResults,
          append: false,
          hasMore: false,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more results. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    matchingPrompt,
    hasClients,
    selectedClientId,
    currentSearchId,
    advisorProfileId,
    loadCount,
    stepperAnswers,
    apiResults,
    matchmakingState,
    preloadedImages,
    filteredProvidersMutation,
    saveResultsMutation,
    setIsLoadingMore,
    setError,
    setLoadMoreMessage,
    setLoadCount,
    setIsPreloadingImages,
    setPreloadedImages,
  ]);

  return { handleLoadMore };
};

